import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Globe, Film, FolderOpen, MessageSquare, Settings, Search as SearchIcon, Navigation, Shield, LogOut, Menu, X, Image, FileText, Users, TrendingUp, Palette, Sliders } from 'lucide-react';
import Dashboard from './admin/Dashboard';
import VideoManager from './admin/VideoManager';
import CategoryManager from './admin/CategoryManager';
import CommentManager from './admin/CommentManager';
import SiteSettingsPanel from './admin/SiteSettingsPanel';
import SEOManager from './admin/SEOManager';
import NavManager from './admin/NavManager';
import BannerManager from './admin/BannerManager';
import PageManager from './admin/PageManager';
import UserManager from './admin/UserManager';
import AnalyticsDashboard from './admin/AnalyticsDashboard';
import AppearanceSettings from './admin/AppearanceSettings';
import AdvancedSettings from './admin/AdvancedSettings';
import DomainManager from './admin/DomainManager';
import StoryManager from './admin/StoryManager';

const AdminPanel = () => {
  const { admin, logout, api } = useAuth();
  const [showLogout, setShowLogout] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ old: '', new: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.new !== pwForm.confirm) { setPwMsg({ text: 'Password baru tidak cocok!', type: 'error' }); return; }
    if (pwForm.new.length < 6) { setPwMsg({ text: 'Password minimal 6 karakter', type: 'error' }); return; }
    setPwLoading(true);
    try {
      await api.put('/admin/password', { old_password: pwForm.old, new_password: pwForm.new });
      setPwMsg({ text: 'Password berhasil diubah!', type: 'success' });
      setPwForm({ old: '', new: '', confirm: '' });
      setTimeout(() => { setShowChangePw(false); setPwMsg({ text: '', type: '' }); }, 2000);
    } catch (err) {
      setPwMsg({ text: err.response?.data?.detail || 'Gagal mengubah password', type: 'error' });
    }
    setPwLoading(false);
  };
  const getInitialSection = () => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'dashboard';
  };
  const [activeSection, setActiveSection] = useState(getInitialSection);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    window.location.hash = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) setActiveSection(hash);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const role = admin?.role || "admin";
  const allMenuItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, section: "overview", roles: ["admin","moderator","editor","viewer"] },
    { id: "analytics", label: "Analytics", icon: TrendingUp, section: "overview", roles: ["admin","moderator"] },
    { id: "videos", label: "Videos", icon: Film, section: "content", roles: ["admin","moderator","editor"] },
    { id: "categories", label: "Categories", icon: FolderOpen, section: "content", roles: ["admin","moderator"] },
    { id: "comments", label: "Comments", icon: MessageSquare, section: "content", roles: ["admin","moderator"] },
    { id: "pages", label: "Pages", icon: FileText, section: "content", roles: ["admin","moderator","editor"] },
    { id: "stories", label: "Cerita Dewasa", icon: FileText, section: "content", roles: ["admin","moderator","editor"] },
    { id: "banners", label: "Banner Ads", icon: Image, section: "marketing", roles: ["admin"] },
    { id: "seo", label: "SEO Manager", icon: SearchIcon, section: "marketing", roles: ["admin"] },
    { id: "users", label: "Users", icon: Users, section: "users", roles: ["admin"] },
    { id: "security", label: "Security", icon: Shield, section: "users", roles: ["admin"] },
    { id: "appearance", label: "Appearance", icon: Palette, section: "customize", roles: ["admin"] },
    { id: "navigation", label: "Navigation", icon: Navigation, section: "customize", roles: ["admin"] },
    { id: "settings", label: "Site Settings", icon: Settings, section: "customize", roles: ["admin"] },
    { id: "domains", label: "Domain Manager", icon: Globe, section: "system", roles: ["admin"] },
    { id: "advanced", label: "Advanced", icon: Sliders, section: "system", roles: ["admin"] },
  ];
  const menuItems = allMenuItems.filter(item => item.roles.includes(role));

  const sections = [
    { id: 'overview', label: 'OVERVIEW' },
    { id: 'content', label: 'CONTENT' },
    { id: 'marketing', label: 'MARKETING' },
    { id: 'users', label: 'USERS & ACCESS' },
    { id: 'customize', label: 'CUSTOMIZATION' },
    { id: 'system', label: 'SYSTEM' },
  ];

  const handleNavClick = (id) => {
    setActiveSection(id);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard': return <Dashboard />;
      case 'analytics': return <AnalyticsDashboard />;
      case 'videos': return <VideoManager />;
      case 'categories': return <CategoryManager />;
      case 'comments': return <CommentManager />;
      case 'users': return <UserManager />;
      case 'banners': return <BannerManager />;
      case 'pages': return <PageManager />;
      case 'appearance': return <AppearanceSettings />;
      case 'settings': return <SiteSettingsPanel />;
      case 'seo': return <SEOManager />;
      case 'navigation': return <NavManager />;
      case 'stories': return <StoryManager />;
      case 'domains': return <DomainManager />;
      case 'advanced': return <AdvancedSettings />;
      case 'security': return <SecurityPanel />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && window.innerWidth <= 768 && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
        <div className="admin-sidebar-header">
          <img loading="lazy" src="/logo.png" alt="Logo" style={{ height: "35px" }} />
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="admin-nav">
          {sections.map((section) => {
            const sectionItems = menuItems.filter(item => item.section === section.id);
            return (
              <div key={section.id} className="nav-section-group">
                {sidebarOpen && <div className="section-divider">{section.label}</div>}
                {sectionItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => handleNavClick(item.id)}
                      title={!sidebarOpen ? item.label : ''}
                    >
                      <Icon size={18} />
                      {sidebarOpen && <span>{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="admin-sidebar-footer">

          <button className="admin-nav-item" onClick={() => setShowChangePw(true)} style={{color:'#f59e0b'}}>
            <Shield size={18} />
            {sidebarOpen && <span>Ganti Password</span>}
          </button>
          <button className="admin-nav-item logout-btn" onClick={() => setShowLogout(true)}>
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
          {sidebarOpen && <p className="admin-user">Logged in as <strong>{admin?.username}</strong></p>}
        </div>
      </aside>

      {/* Mobile hamburger */}
      {!sidebarOpen && (
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
          <Menu size={20} />
        </button>
      )}

      <main className="admin-main">
        <div className="admin-content">
          {renderContent()}
        </div>

      {/* Change Password Popup */}
      {showChangePw && (
        <div style={{position:'fixed',inset:0,zIndex:99999,background:'rgba(0,0,0,0.85)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',animation:'fadeIn 0.2s ease'}}>
          <div style={{background:'linear-gradient(135deg,#12121c,#1a1a28)',border:'1px solid rgba(255,60,60,0.15)',borderRadius:20,padding:'36px 32px',maxWidth:440,width:'90%',boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:48,height:48,borderRadius:'50%',background:'linear-gradient(135deg,rgba(245,158,11,0.15),rgba(255,60,60,0.15))',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Shield size={24} style={{color:'#f59e0b'}} />
                </div>
                <div>
                  <h3 style={{fontSize:18,fontWeight:800,color:'#fff',margin:0}}>Ganti Password</h3>
                  <p style={{fontSize:11,color:'#666',margin:0}}>Login sebagai <strong style={{color:'#f59e0b'}}>{admin?.username}</strong></p>
                </div>
              </div>
              <button onClick={()=>{setShowChangePw(false);setPwMsg({text:'',type:''});setPwForm({old:'',new:'',confirm:''});}} style={{width:32,height:32,borderRadius:'50%',background:'rgba(255,255,255,0.06)',border:'none',color:'#888',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>✕</button>
            </div>

            {pwMsg.text && (
              <div style={{padding:'10px 14px',borderRadius:10,marginBottom:16,fontSize:12,fontWeight:600,background:pwMsg.type==='success'?'rgba(76,175,80,0.1)':'rgba(239,68,68,0.1)',color:pwMsg.type==='success'?'#4caf50':'#ef4444',border:`1px solid ${pwMsg.type==='success'?'rgba(76,175,80,0.2)':'rgba(239,68,68,0.2)'}`}}>
                {pwMsg.type==='success'?'✅':'❌'} {pwMsg.text}
              </div>
            )}

            <form onSubmit={handleChangePw}>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4,display:'block'}}>Password Lama</label>
                <input type="password" value={pwForm.old} onChange={e=>setPwForm({...pwForm,old:e.target.value})} required style={{width:'100%',padding:'11px 14px',borderRadius:10,border:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.04)',color:'#fff',fontSize:14,fontFamily:'Inter,sans-serif',outline:'none',transition:'border 0.2s'}} placeholder="Masukkan password lama" />
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4,display:'block'}}>Password Baru</label>
                <input type="password" value={pwForm.new} onChange={e=>setPwForm({...pwForm,new:e.target.value})} required minLength={6} style={{width:'100%',padding:'11px 14px',borderRadius:10,border:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.04)',color:'#fff',fontSize:14,fontFamily:'Inter,sans-serif',outline:'none',transition:'border 0.2s'}} placeholder="Minimal 6 karakter" />
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:11,fontWeight:700,color:'#888',marginBottom:4,display:'block'}}>Konfirmasi Password Baru</label>
                <input type="password" value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} required minLength={6} style={{width:'100%',padding:'11px 14px',borderRadius:10,border:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.04)',color:'#fff',fontSize:14,fontFamily:'Inter,sans-serif',outline:'none',transition:'border 0.2s'}} placeholder="Ketik ulang password baru" />
              </div>
              <div style={{display:'flex',gap:10}}>
                <button type="button" onClick={()=>{setShowChangePw(false);setPwMsg({text:'',type:''});}} style={{flex:1,padding:'11px',borderRadius:10,fontSize:13,fontWeight:700,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.08)',color:'#aaa',cursor:'pointer',fontFamily:'Inter,sans-serif'}}>Batal</button>
                <button type="submit" disabled={pwLoading} style={{flex:1,padding:'11px',borderRadius:10,fontSize:13,fontWeight:700,background:'linear-gradient(135deg,#f59e0b,#ff3c3c)',border:'none',color:'#fff',cursor:'pointer',fontFamily:'Inter,sans-serif',boxShadow:'0 4px 15px rgba(245,158,11,0.3)',opacity:pwLoading?0.6:1}}>{pwLoading?'Saving...':'Simpan Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Logout Popup */}
      {showLogout && (
        <div style={{position:"fixed",inset:0,zIndex:99999,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",animation:"fadeIn 0.2s ease"}}>
          <div style={{background:"linear-gradient(135deg,#12121c,#1a1a28)",border:"1px solid rgba(255,60,60,0.15)",borderRadius:20,padding:"40px 36px",textAlign:"center",maxWidth:400,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(255,60,60,0.1)",border:"2px solid rgba(255,60,60,0.2)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
              <LogOut size={28} style={{color:"#ff3c3c"}} />
            </div>
            <h3 style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:8}}>Logout?</h3>
            <p style={{fontSize:13,color:"#888",marginBottom:24,lineHeight:1.5}}>Kamu yakin mau keluar dari admin panel?</p>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setShowLogout(false)} style={{padding:"10px 28px",borderRadius:10,fontSize:13,fontWeight:700,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.08)",color:"#aaa",cursor:"pointer",fontFamily:"Inter,sans-serif",transition:"all 0.2s"}}>Batal</button>
              <button onClick={()=>{setShowLogout(false);logout();}} style={{padding:"10px 28px",borderRadius:10,fontSize:13,fontWeight:700,background:"linear-gradient(135deg,#ff3c3c,#ff8a00)",border:"none",color:"#fff",cursor:"pointer",fontFamily:"Inter,sans-serif",boxShadow:"0 4px 15px rgba(255,60,60,0.3)",transition:"all 0.2s"}}>Ya, Logout</button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
};


const SecurityPanel = () => {
  const { api } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [msg, setMsg] = useState("");
  const [bannedIps, setBannedIps] = useState([]);
  const [banIp, setBanIp] = useState("");
  const [banReason, setBanReason] = useState("");
  useEffect(() => { loadBans(); }, []);
  const loadBans = () => { api.get("/admin/banned-ips").then(r => setBannedIps(r.data)).catch(() => {}); };
  const handleChange = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/password", { old_password: oldPw, new_password: newPw }); setMsg("Password changed!"); setOldPw(""); setNewPw(""); }
    catch (err) { setMsg(err.response?.data?.detail || "Error"); }
  };
  const handleBan = async (e) => {
    e.preventDefault();
    if (!banIp.trim()) return;
    try { await api.post("/admin/banned-ips", { ip: banIp.trim(), reason: banReason.trim() }); setBanIp(""); setBanReason(""); loadBans(); }
    catch (err) { alert("Error banning IP"); }
  };
  const handleUnban = async (id) => {
    if (!window.confirm("Unban this IP?")) return;
    await api.delete("/admin/banned-ips/" + id); loadBans();
  };
  return (
    <div className="admin-section">
      <h2 className="section-title">Security</h2>
      <div className="two-col">
        <div className="admin-card">
          <h3 className="card-subtitle">Ban IP Address</h3>
          <form onSubmit={handleBan}>
            <label className="form-label">IP Address</label>
            <input className="form-input" value={banIp} onChange={e => setBanIp(e.target.value)} placeholder="e.g. 192.168.1.1" required />
            <label className="form-label">Reason</label>
            <input className="form-input" value={banReason} onChange={e => setBanReason(e.target.value)} placeholder="Spam, abuse, etc." />
            <button type="submit" className="btn-primary">Ban IP</button>
          </form>
        </div>
      </div>
      {bannedIps.length > 0 && (
        <div className="admin-card" style={{marginTop:16}}>
          <h3 className="card-subtitle">Banned IPs ({bannedIps.length})</h3>
          <table className="admin-table">
            <thead><tr><th>IP</th><th>Reason</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>{bannedIps.map(b => (
              <tr key={b.id}><td style={{fontWeight:700,color:"#ef4444"}}>{b.ip}</td><td>{b.reason||"-"}</td><td style={{fontSize:11,color:"#555"}}>{new Date(b.banned_at).toLocaleDateString()}</td><td><button className="btn-sm btn-edit" onClick={()=>handleUnban(b.id)}>Unban</button></td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
export default AdminPanel;
